import React, { useState, useEffect } from 'react';
import { X, FileText, Save, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { subscriptionService } from '../services/subscriptionService';
import { userService } from '../services/userService';
import { rateService } from '../services/rateService';
import { clubService } from '../services/clubService';
import api from '../services/api';

const SubscriptionForm = ({ isOpen, onClose, onSuccess, subscriptionData = null, isEditing = false }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    user_id: subscriptionData?.user_id || '',
    rate_id: subscriptionData?.rate_id || '',
    year: subscriptionData?.year || new Date().getFullYear(),
    paid: subscriptionData?.paid ?? false
  });

  const [users, setUsers] = useState([]);
  const [rates, setRates] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingRates, setLoadingRates] = useState(false);
  const [errors, setErrors] = useState({});

  const isSuperAdmin = user?.role?.name === 'super_admin';
  const isAdmin = user?.role?.name === 'admin';
  const isClubUser = user?.role?.name === 'club';

  // Cargar datos al abrir el formulario
  useEffect(() => {
    if (isOpen) {
      loadUsers();
      loadRates();
      if (isSuperAdmin) {
        loadClubs();
      }
    }
  }, [isOpen, isSuperAdmin]);

  // Actualizar el formulario cuando cambien los datos de la inscripción
  useEffect(() => {
    if (subscriptionData) {
      setFormData({
        user_id: subscriptionData.user_id || '',
        rate_id: subscriptionData.rate_id || '',
        year: subscriptionData.year || new Date().getFullYear(),
        paid: subscriptionData.paid ?? false
      });
    } else {
      setFormData({
        user_id: '',
        rate_id: '',
        year: new Date().getFullYear(),
        paid: false
      });
    }
    setErrors({});
  }, [subscriptionData, isOpen]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      let response;
      if (isSuperAdmin || isAdmin) {
        // Super admin y admin pueden ver todos los socios
        response = await userService.getUsers({ role: 'member' });
      } else if (isClubUser) {
        // Usuarios de club solo ven socios de su club
        response = await userService.getUsers({ role: 'member' });
      } else {
        response = { data: [] };
      }
      
      const usersList = response.data || [];
      setUsers(Array.isArray(usersList) ? usersList : []);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadRates = async () => {
    setLoadingRates(true);
    try {
      const response = await rateService.getRates();
      const ratesList = response.data || response;
      setRates(Array.isArray(ratesList) ? ratesList : []);
    } catch (error) {
      console.error('Error al cargar tarifas:', error);
      setRates([]);
    } finally {
      setLoadingRates(false);
    }
  };

  const loadClubs = async () => {
    try {
      const response = await clubService.getClubs();
      const clubsList = response.data || response;
      setClubs(Array.isArray(clubsList) ? clubsList : []);
    } catch (error) {
      console.error('Error al cargar clubs:', error);
      setClubs([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.user_id) {
      newErrors.user_id = 'El socio es obligatorio';
    }

    if (!formData.rate_id) {
      newErrors.rate_id = 'La tarifa es obligatoria';
    }

    if (!formData.year) {
      newErrors.year = 'El año es obligatorio';
    } else if (formData.year < 2000 || formData.year > 2100) {
      newErrors.year = 'El año debe estar entre 2000 y 2100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        user_id: formData.user_id,
        rate_id: formData.rate_id,
        year: parseInt(formData.year),
        paid: formData.paid
      };

      const result = isEditing
        ? await subscriptionService.updateSubscription(subscriptionData.id, dataToSend)
        : await subscriptionService.createSubscription(dataToSend);
      
      onSuccess?.(result);
      onClose();
    } catch (error) {
      console.error('Error al guardar la inscripción:', error);
      const errorMessage = error.response?.data?.message || 'Error al guardar la inscripción. Inténtalo de nuevo.';
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ submit: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      user_id: '',
      rate_id: '',
      year: new Date().getFullYear(),
      paid: false
    });
    setErrors({});
    onClose();
  };

  // Filtrar usuarios según permisos
  const filteredUsers = users.filter(u => {
    if (isSuperAdmin || isAdmin) return true;
    if (isClubUser) return u.club_id === user?.club_id;
    return false;
  });

  // Filtrar tarifas según permisos
  const filteredRates = rates.filter(r => {
    if (isSuperAdmin || isAdmin) return true;
    if (isClubUser) return r.club_id === user?.club_id;
    return false;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FileText size={24} className="text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Editar Inscripción' : 'Crear Nueva Inscripción'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error general */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
              <AlertCircle size={20} className="text-red-600 mr-3" />
              <span className="text-red-800">{errors.submit}</span>
            </div>
          )}

          {/* Socio */}
          <div>
            <label htmlFor="user_id" className="block text-sm font-medium text-gray-700 mb-2">
              Socio *
            </label>
            {loadingUsers ? (
              <div className="text-gray-500">Cargando socios...</div>
            ) : (
              <select
                id="user_id"
                name="user_id"
                value={formData.user_id}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.user_id ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Selecciona un socio</option>
                {filteredUsers.map(userItem => (
                  <option key={userItem.id} value={userItem.id}>
                    {userItem.name} {userItem.surname || ''} {userItem.club ? `(${userItem.club.name})` : ''}
                  </option>
                ))}
              </select>
            )}
            {errors.user_id && (
              <p className="mt-1 text-sm text-red-600">{errors.user_id}</p>
            )}
          </div>

          {/* Tarifa */}
          <div>
            <label htmlFor="rate_id" className="block text-sm font-medium text-gray-700 mb-2">
              Tarifa *
            </label>
            {loadingRates ? (
              <div className="text-gray-500">Cargando tarifas...</div>
            ) : (
              <select
                id="rate_id"
                name="rate_id"
                value={formData.rate_id}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.rate_id ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Selecciona una tarifa</option>
                {filteredRates.map(rate => (
                  <option key={rate.id} value={rate.id}>
                    {rate.name} - {rate.price} {rate.currency} {rate.club ? `(${rate.club.name})` : ''}
                  </option>
                ))}
              </select>
            )}
            {errors.rate_id && (
              <p className="mt-1 text-sm text-red-600">{errors.rate_id}</p>
            )}
          </div>

          {/* Año */}
          <div>
            <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-2">
              Año *
            </label>
            <input
              type="number"
              id="year"
              name="year"
              value={formData.year}
              onChange={handleInputChange}
              min="2000"
              max="2100"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.year ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.year && (
              <p className="mt-1 text-sm text-red-600">{errors.year}</p>
            )}
          </div>

          {/* Estado de pago */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="paid"
              name="paid"
              checked={formData.paid}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="paid" className="ml-2 block text-sm text-gray-700">
              Inscripción pagada
            </label>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  {isEditing ? 'Actualizar Inscripción' : 'Crear Inscripción'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionForm;
